import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";

import { useMemo } from "react";

import { twMerge } from "tailwind-merge";

import { faComment } from "@fortawesome/free-regular-svg-icons/faComment";
import { faComments } from "@fortawesome/free-regular-svg-icons/faComments";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import ProfilePicture from "components/ProfilePicture";
import useMediaQuery from "hooks/useMediaQuery";
import highlightText from "utils/highLightText";

import styles from "./style.module.scss";

const FollowButton = dynamic(() => import("components/FollowButton"));
const MessageButton = dynamic(() => import("components/MessageButton"));

type ProfileSnippetProps = {
  profile: any;
  showFollowButton?: boolean;
  showMessageButton?: boolean;
  showCompany?: boolean;
  card?: boolean;
  children?: React.ReactNode;
  full?: boolean;
  onFollow?: (_profile: any) => void;
  onContactIR?: () => void;
  className?: string;
  nameClassName?: string;
  descriptionClassName?: string;
  alwaysShowAvatar?: boolean;
  useComma?: boolean;
  query?: string;
  clickAnywhere?: boolean;
  compactCard?: boolean;
  followButtonAfterName?: boolean;
  customFollowButton?: () => React.ReactNode;
};

const ProfileSnippet = ({
  profile,
  showFollowButton,
  showMessageButton,
  showCompany,
  card,
  full,
  onFollow,
  onContactIR,
  className,
  nameClassName,
  descriptionClassName,
  alwaysShowAvatar,
  useComma,
  query,
  clickAnywhere,
  compactCard,
  followButtonAfterName,
  customFollowButton,
}: ProfileSnippetProps) => {
  const router = useRouter();
  const media = useMediaQuery();

  const followButton = useMemo(() => {
    if (customFollowButton) return customFollowButton();

    return (
      <FollowButton
        profile={profile}
        onFollow={onFollow}
        style={full ? "full" : "small"}
      />
    );
  }, [customFollowButton, full, onFollow, profile]);

  if (!profile) return <></>;

  const {
    slug,
    name,
    title,
    avatarUrl: { small: avatarUrl },
    company,
    description,
  } = profile;

  const highlightedName = query ? highlightText(name, query) : name;
  const highlightedCompanyName = query
    ? highlightText(company?.name, query)
    : company?.name;
  const highlightedDescription = query
    ? highlightText(description, query)
    : description;

  const link = `/${profile.isCompany ? "companies" : "profiles"}/${slug}`;

  if (compactCard) {
    return (
      <div
        className={twMerge(
          styles.container,
          styles.card,
          "flex flex-col justify-between",
          "!max-w-[100px] min-h-[200px] !min-w-[150px]",
          className
        )}
      >
        <div className="text-center w-full">
          <Link
            href={link}
            passHref
            className="flex items-center w-full justify-center"
          >
            <ProfilePicture src={avatarUrl} size={45} />
          </Link>
          <div className={"my-[10px]"}>
            <Link
              href={link}
              className={twMerge(
                styles.link,
                styles.text,
                styles.bold,
                "text-[14px]",
                nameClassName
              )}
            >
              {highlightedName}
            </Link>
            {title ? (
              <div
                className={twMerge(
                  styles.text,
                  styles.fade,
                  "text-[12px]",
                  descriptionClassName
                )}
              >
                {title}
                {showCompany && highlightedCompanyName && (
                  <>
                    {useComma ? ", " : " / "}
                    <Link
                      href={`/companies/${company.slug}`}
                      className={styles.small}
                    >
                      {highlightedCompanyName}
                    </Link>
                  </>
                )}
              </div>
            ) : (
              showCompany &&
              highlightedCompanyName && (
                <div
                  className={twMerge(
                    styles.text,
                    styles.fade,
                    descriptionClassName
                  )}
                >
                  <Link
                    href={`/companies/${company.slug}`}
                    className={styles.small}
                  >
                    {highlightedCompanyName}
                  </Link>
                </div>
              )
            )}
          </div>
        </div>
        <div className="flex space-x-2 items-center justify-center w-full">
          {showFollowButton &&
            (full ? (
              <FollowButton profile={profile} onFollow={onFollow} />
            ) : (
              <div className="mt-[10px]">
                <FollowButton
                  profile={profile}
                  style="card"
                  onFollow={onFollow}
                />
              </div>
            ))}
          {showMessageButton && <MessageButton profile={profile} />}
          {onContactIR && (
            <button
              className={twMerge(
                "bg-white hover:bg-smart-teal text-smart-teal hover:text-white ",
                "border border-solid border-smart-teal ",
                "rounded-sm text-[12px] px-[1em] py-[0.75em]"
              )}
              onClick={onContactIR}
            >
              <FontAwesomeIcon icon={faComment} />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (card) {
    return (
      <div className={twMerge(styles.container, styles.card, className)}>
        <div className={styles.row}>
          <Link href={link} passHref>
            <ProfilePicture
              src={avatarUrl}
              className={twMerge(styles.col, styles.icon)}
            />
          </Link>
          <div className={twMerge(styles.col, styles.full)}>
            <Link
              href={link}
              className={twMerge(
                styles.link,
                styles.text,
                styles.bold,
                "text-[12px]",
                nameClassName
              )}
            >
              {highlightedName}
            </Link>
            {title ? (
              <div
                className={twMerge(
                  styles.text,
                  styles.fade,
                  descriptionClassName
                )}
              >
                {title}
                {showCompany && highlightedCompanyName && (
                  <>
                    {useComma ? ", " : " / "}
                    <Link
                      href={`/companies/${company.slug}`}
                      className={styles.small}
                    >
                      {highlightedCompanyName}
                    </Link>
                  </>
                )}
              </div>
            ) : (
              showCompany &&
              highlightedCompanyName && (
                <div
                  className={twMerge(
                    styles.text,
                    styles.fade,
                    descriptionClassName
                  )}
                >
                  <Link
                    href={`/companies/${company.slug}`}
                    className={styles.small}
                  >
                    {highlightedCompanyName}
                  </Link>
                </div>
              )
            )}
            <div className="flex space-x-2">
              {showFollowButton &&
                (full ? (
                  <FollowButton profile={profile} />
                ) : (
                  <div className="mt-[10px]">
                    <FollowButton profile={profile} style="card" />
                  </div>
                ))}
              {showMessageButton && <MessageButton profile={profile} />}
              {onContactIR && (
                <button
                  className={twMerge(
                    "bg-white hover:bg-smart-teal text-smart-teal hover:text-white ",
                    "border border-solid border-smart-teal",
                    "rounded-full text-[12px] px-[16px] py-[4px] mt-[8px]"
                  )}
                  onClick={onContactIR}
                >
                  <FontAwesomeIcon icon={faComments} />
                  <span className="ml-[8px]">Message</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        styles.container,
        clickAnywhere && "hover:bg-nearly-white !cursor-pointer",
        className
      )}
      onClick={() => clickAnywhere && router.push(link)}
    >
      {(!media.mobile || alwaysShowAvatar) && (
        <Link href={link}>
          <ProfilePicture
            border
            src={avatarUrl}
            className={twMerge(styles.col, styles.icon)}
          />
        </Link>
      )}
      <div className={twMerge(styles.col, styles.full)}>
        <div className="flex">
          <Link
            href={link}
            className={twMerge(
              styles.link,
              styles.text,
              styles.bold,
              "text-[12px]",
              nameClassName
            )}
          >
            {highlightedName}
          </Link>
          {showFollowButton && followButtonAfterName && followButton}
        </div>
        {title ? (
          <div
            className={twMerge(
              styles.text,
              styles.small,
              styles.fade,
              descriptionClassName
            )}
          >
            {title}
            {showCompany && company && (
              <>
                {useComma ? ", " : " / "}
                <Link href={`/companies/${company.slug || company}`}>
                  {highlightedDescription}
                </Link>
              </>
            )}
          </div>
        ) : (
          showCompany &&
          company && (
            <div
              className={twMerge(
                styles.text,
                styles.small,
                styles.fade,
                descriptionClassName
              )}
            >
              <Link href={`/companies/${company.slug || company}`}>
                {highlightedDescription}
              </Link>
            </div>
          )
        )}
      </div>
      <div className="flex space-x-2">
        {showFollowButton && !followButtonAfterName && followButton}
        {showMessageButton && <MessageButton profile={profile} />}
        {onContactIR && (
          <button
            className={twMerge(
              "bg-white hover:bg-smart-teal text-smart-teal hover:text-white ",
              "border border-solid border-smart-teal ",
              "rounded-sm text-[12px] px-[1em] py-[0.75em]"
            )}
            onClick={onContactIR}
          >
            <FontAwesomeIcon icon={faComment} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileSnippet;
